import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Avatar,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';
import { ImageUploadService } from '../../accounting/inventory/services/imageUploadService';
import { getGeneralConfig, saveGeneralConfig, getHeaderConfig } from '../../../services/ecommerceApi';

interface MetaImageSettingsProps {
  companyId: string | null;
}

const MAX_META_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB

const MetaImageSettings: React.FC<MetaImageSettingsProps> = ({ companyId }) => {
  const { t } = useTranslation('salesSystem');
  const [currentMetaImage, setCurrentMetaImage] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isApplyingLogo, setIsApplyingLogo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const [generalResponse, headerResponse] = await Promise.all([
          getGeneralConfig(companyId),
          getHeaderConfig(companyId).catch(() => null),
        ]);
        if (cancelled) return;
        if (generalResponse?.data?.metaImage) {
          setCurrentMetaImage(generalResponse.data.metaImage);
        }
        const headerLogo = headerResponse?.data?.logoUrl;
        if (headerLogo) {
          setLogoUrl(headerLogo);
        }
      } catch (err) {
        console.error('❌ [META-IMAGE] Mevcut ayarlar getirilemedi:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setSuccess(null);
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError(t('ecommerceGeneral.metaImage.errorInvalidType'));
      return;
    }
    if (file.size > MAX_META_IMAGE_SIZE) {
      setError(t('ecommerceGeneral.metaImage.errorTooLarge'));
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result as string);
    reader.readAsDataURL(file);
  }, [t]);

  const handleUpload = useCallback(async () => {
    if (!selectedFile || !companyId) {
      setError(t('ecommerceGeneral.metaImage.errorSelectFirst'));
      return;
    }

    setIsUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const uploaded = await ImageUploadService.uploadImages([selectedFile]);
      const downloadURL = uploaded?.[0]?.originalUrl;

      if (!downloadURL) {
        throw new Error('Görsel servisi geçerli bir URL döndürmedi.');
      }

      const response = await saveGeneralConfig({
        companyId,
        metaImage: downloadURL,
      });

      if (response?.success !== false) {
        setCurrentMetaImage(downloadURL);
        setSelectedFile(null);
        setPreview(null);
        setSuccess(t('ecommerceGeneral.metaImage.successSaved'));
      } else {
        setError(t('ecommerceGeneral.metaImage.errorSaveFailed'));
      }
    } catch (err: any) {
      console.error('❌ [META-IMAGE] Yükleme hatası:', err);
      setError(err?.message || t('ecommerceGeneral.metaImage.errorUpload'));
    } finally {
      setIsUploading(false);
    }
  }, [selectedFile, companyId, t]);

  const handleUseLogo = useCallback(async () => {
    if (!companyId || !logoUrl) return;

    setIsApplyingLogo(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await saveGeneralConfig({
        companyId,
        metaImage: logoUrl,
      });

      if (response?.success !== false) {
        setCurrentMetaImage(logoUrl);
        setSelectedFile(null);
        setPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        setSuccess(t('ecommerceGeneral.metaImage.successLogoApplied'));
      } else {
        setError(t('ecommerceGeneral.metaImage.errorSaveFailed'));
      }
    } catch (err: any) {
      console.error('❌ [META-IMAGE] Logo uygulama hatası:', err);
      setError(err?.message || t('ecommerceGeneral.metaImage.errorUpload'));
    } finally {
      setIsApplyingLogo(false);
    }
  }, [companyId, logoUrl, t]);

  const handleDelete = useCallback(async () => {
    if (!companyId) return;

    if (selectedFile && !currentMetaImage) {
      setSelectedFile(null);
      setPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setIsDeleting(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await saveGeneralConfig({
        companyId,
        metaImage: '',
      });

      if (response?.success !== false) {
        setCurrentMetaImage(null);
        setSelectedFile(null);
        setPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        setSuccess(t('ecommerceGeneral.metaImage.successRemoved'));
      } else {
        setError(t('ecommerceGeneral.metaImage.errorRemoveFailed'));
      }
    } catch (err: any) {
      console.error('❌ [META-IMAGE] Silme hatası:', err);
      setError(err?.message || t('ecommerceGeneral.metaImage.errorRemove'));
    } finally {
      setIsDeleting(false);
    }
  }, [companyId, selectedFile, currentMetaImage, t]);

  const previewSrc = preview || currentMetaImage || undefined;
  const hasMetaImage = Boolean(selectedFile || currentMetaImage);
  const busy = isUploading || isDeleting || isApplyingLogo;
  const showLogoSuggestion = Boolean(logoUrl) && logoUrl !== currentMetaImage;

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <ImageIcon color="primary" sx={{ mr: 1.5 }} />
        <Typography variant="h6">{t('ecommerceGeneral.metaImage.title')}</Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('ecommerceGeneral.metaImage.subtitle')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {showLogoSuggestion && (
        <Alert
          icon={<AutoAwesomeIcon fontSize="inherit" />}
          severity="info"
          sx={{ mb: 2, alignItems: 'center' }}
          action={
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              onClick={handleUseLogo}
              disabled={busy}
              startIcon={isApplyingLogo ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {t('ecommerceGeneral.metaImage.useLogoButton')}
            </Button>
          }
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              src={logoUrl || undefined}
              variant="rounded"
              sx={{ width: 40, height: 40, bgcolor: '#fff', border: '1px solid #e0e0e0' }}
            >
              <ImageIcon color="disabled" />
            </Avatar>
            <span>{t('ecommerceGeneral.metaImage.suggestionText')}</span>
          </Box>
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Avatar
          src={previewSrc}
          variant="rounded"
          sx={{ width: 96, height: 96, bgcolor: '#f5f5f5', border: '1px solid #e0e0e0' }}
        >
          <ImageIcon color="disabled" />
        </Avatar>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            hidden
            onChange={handleFileChange}
          />
          <Button
            variant="outlined"
            startIcon={<ImageIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
          >
            {t('ecommerceGeneral.metaImage.selectFile')}
          </Button>
          <Button
            variant="contained"
            startIcon={isUploading ? <CircularProgress size={18} color="inherit" /> : <UploadIcon />}
            onClick={handleUpload}
            disabled={!selectedFile || busy}
          >
            {isUploading ? t('ecommerceGeneral.metaImage.uploading') : t('ecommerceGeneral.metaImage.uploadSave')}
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={isDeleting ? <CircularProgress size={18} color="inherit" /> : <DeleteOutlineIcon />}
            onClick={handleDelete}
            disabled={!hasMetaImage || busy}
          >
            {isDeleting ? t('ecommerceGeneral.metaImage.removing') : t('ecommerceGeneral.metaImage.remove')}
          </Button>
        </Box>
      </Box>

      {selectedFile && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          {t('ecommerceGeneral.metaImage.selectedFile', { name: selectedFile.name })}
        </Typography>
      )}
    </Paper>
  );
};

export default MetaImageSettings;
